import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContextType } from '../types';
import { api } from '../lib/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthContextType['user']>(null);
  const [permissions, setPermissions] = useState<AuthContextType['permissions']>({
    canManageStaff: false,
    canDeleteLinks: false,
    canEditLinks: false,
    canViewAnalytics: false,
    canManageCampaigns: false,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [logoutUrl, setLogoutUrl] = useState('/');
  const [devUserEmail, setDevUserEmailState] = useState(
    localStorage.getItem('bro_dev_user_email') || 'marketing@brototype.com'
  );

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{
        user: any;
        permissions: any;
        logoutUrl: string;
      }>('/auth/me');

      setUser(data.user);
      setPermissions(data.permissions);
      setLogoutUrl(data.logoutUrl);
    } catch (err) {
      console.error('Failed to load authenticated user profile:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const setDevUserEmail = (email: string) => {
    localStorage.setItem('bro_dev_user_email', email);
    setDevUserEmailState(email);
    fetchProfile();
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        permissions,
        isLoading,
        logoutUrl,
        devUserEmail,
        setDevUserEmail,
        refreshUser: fetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
